<template>
	<div class="support-settings lok-scrollbar">
		<div class="support-settings__email">
			<span>{{ `${translate("ui.support_email")}: ` }}</span>
			<span @click="copy" class="support-email">{{ supportEmail }}</span>
		</div>
		<ToastMessage
			class="support-mail-message"
			:show-message="!!toastMessage"
			:message="toastMessage ?? ''"
		/>
		<AppTextButton
			class="support-settings__button"
			:type="StatusTypes.DEFAULT"
			:text="translate('button.facebook.non_translatable').toUpperCase()"
			@click="goToUrl(fbPage)"
		/>
		<AppTextButton
			class="support-settings__button"
			:type="StatusTypes.PRIMARY"
			:text="translate('button.discord.non_translatable').toUpperCase()"
			@click="showDiscordPopup"
		/>
		<div v-if="!discordRewardClaimed" v-html="discordRewardText()"></div>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import { ResourceType } from "@/game/entities/config/resources";
import SoundService from "@/game/SoundService";
import { ToastPlacement } from "@/game/ToastMessage";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { discordLink, fbPage, supportEmail } from "@/utils/url";
import { storeToRefs } from "pinia";
import { markRaw, ref } from "vue";

const soundService = markRaw(SoundService.getInstance());

const toastMessage = ref<string | null>(null);
const userStore = useUserStore();
const { discordRewardClaimed } = storeToRefs(userStore);
const popupStore = usePopupStore();

const copy = async (): Promise<void> => {
	soundService.playGenerickClick();
	await navigator.clipboard.writeText(supportEmail);
	handleMessage("copied_to_clipboard");
};

const handleMessage = (message: string): void => {
	toastMessage.value = message;
	setTimeout((): void => {
		toastMessage.value = null;
	}, 1_000);
};

const goToUrl = (url: string): void => {
	window.open(url, "_blank");
};

const discordRewardText = (): string => {
	let body = translate("ui.join_discord_prompt");
	body = body.replace("{number}", "<strong>100</strong>");
	body = body.replace("{icon}", "<span class='icon-gems-14'></span>");
	return body;
};
const showDiscordPopup = (): void => {
	if (!discordRewardClaimed.value) {
		let body = translate("ui.join_reward");
		body = body.replace("{number}", "<strong>100</strong>");
		body = body.replace("{icon}", "<span class='icon-gems-14'></span>");
		const { increase } = useResourcesStore();
		const callback = (): void => {
			popupStore.hidePopup();
		};
		popupStore.showPopup({
			id: PopupDictionary.DISCORD_REWARD,
			type: PopupType.DISCORD_REWARD,
			heading: translate("button.discord.non_translatable"),
			body,
			skin: SkinTypes.LIGHT,
			actions: [
				{
					label: translate("button.back"),
					buttonType: StatusTypes.INFO,
					callback: (): void => {
						callback();
					},
				},
				{
					label: translate("button.join"),
					buttonType: StatusTypes.PRIMARY,
					callback: (): void => {
						callback();
						userStore.setDiscordRewardClaimed(true);
						increase(ResourceType.GEMS, 100, "discord_reward");
						useEmitter().emit(EmitterEvents.PhaserTileClick, {
							x: 0,
							y: 0,
							reward: {
								[ResourceType.GEMS.toLowerCase() as string]: 100,
							},
							placement: ToastPlacement.TOP_CENTER,
						});
						useSocketService()
							.emitSaveRequest({
								stores: [EmitStores.USER],
							})
							.then((): void => {
								//
								goToUrl(discordLink);
							});
					},
				},
			],
			height: 12.5,
			isCentered: true,
			isPaddingless: true,
		});
		return;
	}
	goToUrl(discordLink);
};
</script>

<style lang="scss" scoped>
.support-settings {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 0.75rem;
	padding: 1.25rem;
	&__email {
		display: flex;
		position: relative;
		gap: 0.5rem;
		margin-bottom: 1rem;
		& > span {
			color: #fff3dd;
			font-size: 0.906rem;
			&:nth-child(2) {
				cursor: pointer;
				font-weight: bold;
			}
		}
	}
	&__button {
		width: 11rem;
	}
}
.support-mail-message {
	position: absolute;
	top: -0.25rem;
	right: 0.6rem;
}
</style>
